import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Field,
  PageTitle,
  Notice,
  ErrorNotice,
  ButtonLink,
  QueryState,
  Image,
  Select,
  DatePicker,
  useUnsavedChanges,
  useToast,
} from '@yingbaba/ui';
import { Check, ArrowRight, ArrowLeft, Sparkles, ShieldCheck } from '@yingbaba/icons';
import { api, post, postIdempotent, useData } from '@yingbaba/api';
import { categories, cityOptions, siteURL, money, beijingToday } from '@yingbaba/utils';
import type { Demand, Portfolio, ServicePackage, ServicePackageItem } from '@yingbaba/types';
type ServicePackageView = { package: ServicePackage; items: ServicePackageItem[] };
const schema = z
  .object({
    title: z.string().min(4, '项目名称至少 4 个字符').max(100),
    category: z.string().min(1),
    city: z.string().min(1),
    budget_min: z.number().min(0, '预算不能为负数'),
    budget_max: z.number().positive('请输入预算上限'),
    deadline: z
      .string()
      .min(1, '请选择交付日期')
      .refine((value) => value >= beijingToday(), '交付日期不能早于今天'),
    duration: z.string(),
    description: z.string().min(20, '请至少用 20 个字符描述需求').max(8000),
    deliverables: z.string().min(3, '请说明需要的交付物'),
    tags: z.string(),
  })
  .refine((v) => v.budget_max >= v.budget_min, {
    message: '预算上限不能低于下限',
    path: ['budget_max'],
  });
type FormValues = z.infer<typeof schema>;
export function Publish() {
  const [params] = useSearchParams();
  const editing = params.get('edit');
  const servicePackageID = params.get('package') || '';
  const packageProviderID = params.get('provider') || '';
  const packageCity = params.get('city') || '';
  const referenceCaseID = params.get('case') || '';
  const [unlinkPackage, setUnlinkPackage] = useState(false);
  const existing = useData<Demand>('/demands/' + editing, !!editing);
  const packageList = useData<ServicePackageView[]>(
    '/providers/' + packageProviderID + '/packages',
    !!servicePackageID && !!packageProviderID && !editing,
  );
  const referenceCase = useData<Portfolio>(
    '/cases/' + referenceCaseID,
    !!referenceCaseID && !editing,
  );
  const selectedPackage = packageList.data?.find((v) => v.package.id === servicePackageID)?.package;
  const [step, setStep] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null),
    [done, setDone] = useState(false);
  const {
    register,
    control,
    trigger,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      category: '品牌宣传片',
      city: '上海',
      budget_min: 10000,
      budget_max: 30000,
      deadline: '',
      duration: '30–45 天',
      description: '',
      deliverables: '90 秒主片，15 秒短视频，横竖版各一套',
      tags: '创意策划,拍摄制作,后期剪辑',
    },
  });
  const initialized = useRef('');
  const saving = useRef(false);
  const savedID = useRef(editing || '');
  const retainedPackage = useRef<string | undefined>(undefined);
  const createAttempt = useRef<{ input: string; key: string } | null>(null);
  const toast = useToast();
  useUnsavedChanges({
    dirty: !done && (isDirty || unlinkPackage),
    busy,
    save: () => save(false),
    discard: () => {
      reset(getValues());
      setUnlinkPackage(false);
    },
  });
  useEffect(() => {
    // Once editing starts, late reference data must never replace the form,
    // including after a successful save clears isDirty.
    if (isDirty && !initialized.current) initialized.current = 'manual';
  }, [isDirty]);
  useEffect(() => {
    if (existing.data && !isDirty && !initialized.current) {
      initialized.current = `edit:${editing}`;
      const d = existing.data;
      reset({ ...d, budget_min: d.budget_min_minor / 100, budget_max: d.budget_max_minor / 100 });
    }
  }, [editing, existing.data, reset, isDirty]);
  useEffect(() => {
    if (!editing && selectedPackage && !isDirty && !initialized.current) {
      initialized.current = `package:${servicePackageID}`;
      const pkg = selectedPackage;
      const duration =
        pkg.estimated_days <= 15
          ? '7–15 天'
          : pkg.estimated_days <= 30
            ? '15–30 天'
            : pkg.estimated_days <= 45
              ? '30–45 天'
              : pkg.estimated_days <= 60
                ? '45–60 天'
                : '双方协商';
      const min = pkg.price_min_minor > 0 ? pkg.price_min_minor / 100 : 10000;
      const max =
        pkg.pricing_mode === 'range' && pkg.price_max_minor > 0
          ? pkg.price_max_minor / 100
          : pkg.price_min_minor > 0
            ? pkg.price_min_minor / 100
            : 30000;
      reset({
        title: pkg.name + '项目',
        category: pkg.category,
        city: packageCity || '上海',
        budget_min: min,
        budget_max: Math.max(min, max),
        deadline: '',
        duration,
        description: pkg.summary,
        deliverables: pkg.deliverables || '按照服务产品约定并结合最终方案确认',
        tags: pkg.category,
      });
    }
  }, [editing, selectedPackage, packageCity, reset, servicePackageID, isDirty]);
  useEffect(() => {
    if (!editing && !servicePackageID && referenceCase.data && !isDirty && !initialized.current) {
      initialized.current = `case:${referenceCaseID}`;
      const c = referenceCase.data;
      reset({
        title: `${c.title} 同类项目`,
        category: c.category || '品牌宣传片',
        city: '上海',
        budget_min: 10000,
        budget_max: 30000,
        deadline: '',
        duration: '30–45 天',
        description: `参考案例：「${c.title}」。希望沿用其中的视觉方向和创作气质，并结合本项目品牌、产品与传播目标重新设计执行方案。`,
        deliverables: '主片及适配传播渠道的短版素材，具体规格在方案阶段确认',
        tags: c.tags || [c.category, c.industry].filter(Boolean).join(','),
      });
    }
  }, [editing, referenceCase.data, reset, servicePackageID, referenceCaseID, isDirty]);
  async function next() {
    const fields: (keyof FormValues)[][] = [
      ['title', 'category', 'city', 'budget_min', 'budget_max', 'deadline', 'duration'],
      ['description', 'deliverables', 'tags'],
    ];
    if (await trigger(fields[step])) setStep(step + 1);
  }
  async function save(publish: boolean): Promise<boolean> {
    if (saving.current) return false;
    saving.current = true;
    try {
      if (!(await trigger())) {
        const fields = schema.safeParse(getValues());
        const first = fields.success ? '' : String(fields.error.issues[0]?.path[0] || '');
        setStep(['description', 'deliverables', 'tags'].includes(first) ? 1 : 0);
        return false;
      }
      setBusy(true);
      setError(null);
      const v = getValues();
      const data = {
        ...v,
        budget_min_minor: Math.round(v.budget_min * 100),
        budget_max_minor: Math.round(v.budget_max * 100),
        service_package_id: unlinkPackage
          ? ''
          : (retainedPackage.current ??
            (editing ? existing.data?.service_package_id || '' : servicePackageID)),
        reference_portfolio_id: editing
          ? existing.data?.reference_portfolio_id || ''
          : referenceCaseID,
        max_shortlist: 3,
      };
      let id = savedID.current;
      if (!id) {
        const input = JSON.stringify(data);
        if (!createAttempt.current || createAttempt.current.input !== input)
          createAttempt.current = { input, key: crypto.randomUUID() };
        const d = await postIdempotent<Demand>('/demands', data, createAttempt.current.key);
        id = d.id;
        savedID.current = id;
      } else await api(`/demands/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      retainedPackage.current = data.service_package_id;
      reset(v);
      if (unlinkPackage) setUnlinkPackage(false);
      if (publish) {
        try {
          await post(`/demands/${id}/publish`);
        } catch (error) {
          const latest = await api<Demand>(`/demands/${id}`).catch(() => null);
          if (!latest || !['pending_review', 'published'].includes(latest.status)) throw error;
        }
        setDone(true);
      } else toast('草稿已保存，可以继续编辑');
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  if (editing && !existing.data)
    return (
      <div className="container section">
        <QueryState query={existing}>{null}</QueryState>
      </div>
    );
  if (editing && existing.data && !['draft', 'rejected'].includes(existing.data.status))
    return (
      <div className="container section">
        <Notice>此需求已进入审核或发布流程，请前往工作台查看当前状态。</Notice>
        <ButtonLink to={siteURL('work', '/demands')}>返回我的需求</ButtonLink>
      </div>
    );
  if (done)
    return (
      <div className="container section">
        <div
          className="panel"
          style={{ maxWidth: 680, margin: '35px auto', textAlign: 'center', padding: '50px 30px' }}
        >
          <span className="icon-tile" style={{ margin: '0 auto 22px' }}>
            <Check size={25} />
          </span>
          <h1>好想法，已经迈出第一步。</h1>
          <p className="muted" style={{ margin: '18px 0 28px' }}>
            你的需求已提交审核。审核结果和团队合作意向，
            <br />
            都会出现在你的工作台。
          </p>
          <ButtonLink to={siteURL('work', '/demands')}>
            管理我的需求
            <ArrowRight size={16} />
          </ButtonLink>
        </div>
      </div>
    );
  return (
    <div className="container section publish-page">
      <PageTitle
        title="说说你的拍片想法"
        description="不必事事专业，先把期待告诉我们。清晰的需求，是好合作的开始。"
      />
      {servicePackageID && !editing && (
        <QueryState query={packageList}>
          {selectedPackage && (
            <Notice>
              {`当前需求基于服务产品「${selectedPackage.name}」。分类、参考预算、制作周期和标准交付范围已经预填，你仍可以根据实际项目补充和调整。`}
            </Notice>
          )}
        </QueryState>
      )}
      {referenceCaseID && !editing && (
        <QueryState query={referenceCase}>
          {referenceCase.data && (
            <div className="case-reference-card">
              <Image src={referenceCase.data.cover} alt={referenceCase.data.title} />
              <div>
                <span className="eyebrow">REFERENCE CASE</span>
                <strong>参考案例：{referenceCase.data.title}</strong>
                <p className="muted small">
                  已根据案例预填类型、创作方向和标签。发布后所有符合条件的服务商仍可参与，不会自动锁定原制作团队。
                </p>
              </div>
            </div>
          )}
        </QueryState>
      )}
      {editing && existing.data?.service_package_id && retainedPackage.current !== '' && (
        <Notice>
          此需求关联了服务产品。编辑时默认保留关联。
          <label style={{ display: 'block', marginTop: 8 }}>
            <input
              type="checkbox"
              disabled={busy}
              checked={unlinkPackage}
              onChange={(event) => setUnlinkPackage(event.target.checked)}
            />{' '}
            我确认解除与原服务产品的关联
          </label>
        </Notice>
      )}
      <div className="steps">
        {['项目基本信息', '创意与交付要求', '确认并提交'].map((v, i) => (
          <div
            className={`step ${step === i ? 'active' : ''} ${step > i ? 'complete' : ''}`}
            key={v}
          >
            <span>{step > i ? <Check size={14} /> : i + 1}</span>
            {v}
          </div>
        ))}
      </div>
      <div className="form-layout">
        <div className="panel">
          <form
            aria-busy={busy}
            onSubmit={(e) => {
              e.preventDefault();
              if (step < 2) next();
              else save(true);
            }}
          >
            <fieldset className="publish-fields" disabled={busy} aria-label="拍片需求资料">
              <div className="form-stack">
                {step === 0 && (
                  <>
                    <Field label="项目名称 *" htmlFor="title" error={errors.title?.message}>
                      <input
                        id="title"
                        className="input"
                        placeholder="例如：生活方式品牌年度形象短片"
                        {...register('title')}
                      />
                    </Field>
                    <div className="grid-2">
                      <Field label="拍摄类型 *" htmlFor="category">
                        <Controller
                          name="category"
                          control={control}
                          render={({ field }) => (
                            <Select id="category" {...field} validationBehavior="aria">
                              {categories.map((c) => (
                                <option key={c}>{c}</option>
                              ))}
                            </Select>
                          )}
                        />
                      </Field>
                      <Field label="拍摄城市 *" htmlFor="city">
                        <Controller
                          name="city"
                          control={control}
                          render={({ field }) => (
                            <Select id="city" {...field} validationBehavior="aria">
                              {cityOptions.map((c) => (
                                <option key={c}>{c}</option>
                              ))}
                            </Select>
                          )}
                        />
                      </Field>
                    </div>
                    <div className="grid-2">
                      <Field
                        label="预算下限（元）*"
                        htmlFor="budget_min"
                        error={errors.budget_min?.message}
                      >
                        <div className="input-affix">
                          <input
                            id="budget_min"
                            type="number"
                            min="0"
                            step="1"
                            className="input"
                            {...register('budget_min', { valueAsNumber: true })}
                          />
                          <span aria-hidden="true">元</span>
                        </div>
                      </Field>
                      <Field
                        label="预算上限（元）*"
                        htmlFor="budget_max"
                        error={errors.budget_max?.message}
                      >
                        <div className="input-affix">
                          <input
                            id="budget_max"
                            type="number"
                            min="1"
                            step="1"
                            className="input"
                            {...register('budget_max', { valueAsNumber: true })}
                          />
                          <span aria-hidden="true">元</span>
                        </div>
                      </Field>
                    </div>
                    <div className="grid-2">
                      <Field
                        label="期望交付日期 *"
                        htmlFor="deadline"
                        error={errors.deadline?.message}
                      >
                        <Controller
                          name="deadline"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              id="deadline"
                              {...field}
                              required
                              min={beijingToday()}
                              validationBehavior="aria"
                            />
                          )}
                        />
                      </Field>
                      <Field label="预期制作周期" htmlFor="duration">
                        <Controller
                          name="duration"
                          control={control}
                          render={({ field }) => (
                            <Select id="duration" {...field} validationBehavior="aria">
                              {['7–15 天', '15–30 天', '30–45 天', '45–60 天', '双方协商'].map(
                                (v) => (
                                  <option key={v}>{v}</option>
                                ),
                              )}
                            </Select>
                          )}
                        />
                      </Field>
                    </div>
                  </>
                )}
                {step === 1 && (
                  <>
                    <Field
                      label="详细需求 *"
                      htmlFor="description"
                      hint="建议说明品牌背景、目标受众、核心信息、喜欢的风格及使用渠道。"
                      error={errors.description?.message}
                    >
                      <textarea
                        id="description"
                        rows={8}
                        placeholder="我们希望通过这支影片…"
                        {...register('description')}
                      />
                    </Field>
                    <Field
                      label="交付内容 *"
                      htmlFor="deliverables"
                      error={errors.deliverables?.message}
                    >
                      <textarea id="deliverables" {...register('deliverables')} />
                    </Field>
                    <Field label="需要的服务" htmlFor="tags" hint="用中文或英文逗号分隔服务内容。">
                      <input id="tags" className="input" {...register('tags')} />
                    </Field>
                  </>
                )}
                {step === 2 && (
                  <>
                    <h2 style={{ fontSize: 24 }}>{getValues('title')}</h2>
                    <div className="detail-facts">
                      <div>
                        <span>拍摄类型</span>
                        {getValues('category')}
                      </div>
                      <div>
                        <span>城市</span>
                        {getValues('city')}
                      </div>
                      <div>
                        <span>预算</span>
                        {money(getValues('budget_min') * 100)} –{' '}
                        {money(getValues('budget_max') * 100)}
                      </div>
                      <div>
                        <span>交付日期</span>
                        {getValues('deadline')}
                      </div>
                    </div>
                    <h3>需求说明</h3>
                    <p className="prose">{getValues('description')}</p>
                    <h3>交付内容</h3>
                    <p className="prose">{getValues('deliverables')}</p>
                    <Notice>
                      需求提交后进入审核，审核通过后公开展示。联系方式、未公开商业资料请在项目内沟通。
                    </Notice>
                  </>
                )}
                <ErrorNotice error={error} />
              </div>
              <div className="form-actions">
                {step > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => setStep(step - 1)}
                  >
                    <ArrowLeft size={15} />
                    上一步
                  </Button>
                )}
                {step === 2 && (
                  <Button
                    type="button"
                    variant="secondary"
                    loading={busy}
                    onClick={() => save(false)}
                  >
                    保存草稿
                  </Button>
                )}
                <Button type="submit" loading={busy}>
                  {step < 2 ? '下一步' : '确认提交审核'}
                  <ArrowRight size={15} />
                </Button>
              </div>
            </fieldset>
          </form>
        </div>
        <aside className="form-aside">
          <span className="icon-tile amber">
            <Sparkles size={23} />
          </span>
          <h3>好需求，不必很复杂</h3>
          <p>说清楚「拍给谁看」「想表达什么」「在哪里使用」，比堆砌专业词汇更有帮助。</p>
          <hr className="divider" />
          <div className="row">
            <ShieldCheck size={18} />
            <strong>让合作更透明</strong>
          </div>
          <p style={{ marginTop: 12 }}>
            通过团队主页、作品和站内沟通了解合作方。建立协作项目后，统一管理任务、素材与推广；交易由双方自主安排。
          </p>
        </aside>
      </div>
    </div>
  );
}
